import { all, call, put, takeEvery, take, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { isAIMode, isApplicationLoading } from 'src/selectors';
import { noPermissions as noPermissionsAlert } from 'src/components/alert/slice';
import { getResources } from 'src/services/coamClient';
import { RELOAD } from 'src/store/actions/actionTypes';
import {
    completeLoadingApplication,
    setResources,
    loadApplication,
    setTenant,
    setUserSettings,
    confirmImageAI,
    changeMode,
    toggleShowCreateSceneModal,
    setAIModalOpen,
} from 'src/slice';
import { noSurfacesEnabled } from 'src/settings';
import { getPurposeSettings } from './services/linkClient';
import idGeneratorAtor from './util/idGeneratorAtor';
import { uploadBackgrounds } from './pages/editor/components/backgrounds/slice';
import { getAllBackgrounds } from './selectors/layers';
import { getSdk } from './sdk';

const idGenerator = idGeneratorAtor('BackgroundButton');
const idAddGenerator = idGeneratorAtor('aiNewBackground');

type CoamResources = Record<string, string[]>;

type SetResourcesPayload = {
    resources: CoamResources;
    areSurfacesRequired: boolean;
    type?: string;
    id?: string;
};

type SetTenantPayload = {
    type: string;
    id: string;
};

function* initializeCoam(): Generator<any, any, any> {
    const resources: CoamResources = yield* call(getResources, getSdk());
    if (!Object.keys(resources).length) {
        return yield* put(noPermissionsAlert());
    }
    const actionPayload: SetResourcesPayload = {
        resources,
        areSurfacesRequired: noSurfacesEnabled ? localStorage.getItem('settingNoSurfaces') !== 'true' : true,
    };

    // Pull the last selected coam option and check that the user has permission for it.
    let tenantId: string | undefined;
    let tenantType: string | undefined;
    const coam = localStorage.getItem('coamSelection');
    if (coam) {
        [tenantType, tenantId] = coam.split(',');
    }
    if (tenantType && resources[tenantType] && resources[tenantType].includes(tenantId!)) {
        Object.assign(actionPayload, {
            type: tenantType,
            id: tenantId,
        });
    } else {
        // If there is at least one tenant type and it's ids aren't empty populate the display
        const firstTenantType = Object.keys(resources).find((key) => resources[key].length);
        if (firstTenantType) {
            Object.assign(actionPayload, { type: firstTenantType, id: resources[firstTenantType][0] });
        }
    }

    return yield* put(setResources(actionPayload as any));
}

function* loadApp(): Generator<any, void, any> {
    const preloadState = localStorage.getItem('expiredState');
    if (preloadState) {
        localStorage.removeItem('expiredState');
        yield* put({ type: RELOAD, payload: JSON.parse(preloadState) });
    } else {
        yield* call(initializeCoam);
    }
    yield* put(completeLoadingApplication());
}

// This function GUARANTEES that, when this is "yield call()"ed, that the application load will be complete once it is done yielding.
export function* awaitApplicationLoad(): Generator<any, void, any> {
    const loading = yield* select(isApplicationLoading);
    if (loading) {
        yield* take(completeLoadingApplication);
    }
}

function* getTenantMerchandisingSettings({ payload }: PayloadAction<SetTenantPayload>): Generator<any, void, any> {
    if (payload && payload.type && payload.id) {
        const data: any = yield* call(getPurposeSettings, getSdk(), payload.type, payload.id);
        yield* put(setUserSettings(data));
    }
}

function* changeModals({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const isModalOpen = yield* select(isAIMode);
    yield* put(setAIModalOpen(payload === 'ai'));
    if ((isModalOpen && payload !== 'ai') || (!isModalOpen && payload === 'ai')) {
        yield* put(toggleShowCreateSceneModal());
    }
}

function* createSceneFromAI({ payload }: PayloadAction<string>): Generator<any, void, any> {
    if (payload) {
        const response: Response = yield* call(fetch, payload);
        const blob: Blob = yield* call([response, response.blob]);
        (blob as any).name = `imageGeneratedByAI-${Date.now()}`;
        (blob as any).lastModifiedDate = new Date();
        const files = yield* select(getAllBackgrounds);
        const isFirstTime = files.length === 0;
        const newFiles = [
            {
                id: isFirstTime ? idGenerator() : idAddGenerator(),
                file: blob,
                isFirstUpload: isFirstTime,
            },
        ];
        yield* put(uploadBackgrounds(newFiles as any));
    }
}

export default function* applicationSagas() {
    return yield* all([
        yield* takeEvery(loadApplication, loadApp),
        yield* takeEvery(setTenant as any, getTenantMerchandisingSettings),
        yield* takeEvery(setResources as any, getTenantMerchandisingSettings),
        yield* takeEvery(confirmImageAI, createSceneFromAI),
        yield* takeEvery(changeMode, changeModals),
    ]);
}
